class CartDrawer extends HTMLElement {
    constructor() {
        super();
 
        this.addEventListener(
            "keyup",
            (evt) => evt.code === "Escape" && this.close()
        );
        this.querySelector("#CartDrawer-Overlay").addEventListener(
            "click",
            this.close.bind(this)
        );
        this.setHeaderCartIconAccessibility();
        this.preventAutoOpenOnLoad();
    }
 
    // The cart drawer must never be open on initial page load. The theme only
    // opens it on a real user action (clicking the cart icon) or right after an
    // add-to-cart. Some injected third-party app scripts (e.g. GoKwik kwikpass /
    // Razorpay Magic Checkout) erroneously add the `active` class on load — and
    // they may do so on an arbitrary delay (observed ~2s in), so timed checks
    // can lose the race. Instead we watch the drawer's class attribute and strip
    // `active` the moment it appears, until the shopper actually engages with the
    // page. Because every legitimate open is preceded by a user interaction, this
    // guard can never suppress an intended open, and it stops watching as soon as
    // the shopper engages so it costs nothing thereafter.
    preventAutoOpenOnLoad() {
        this.userEngaged = false;
 
        const guardClose = () => {
            if (this.userEngaged) return;
            if (!this.classList.contains("active")) return;
            this.classList.remove("active", "animate");
            document.body.classList.remove(
                "cart-overflow-hidden",
                "overflow-hidden",
                "overflow-clip"
            );
        };
 
        const observer = new MutationObserver(guardClose);
        observer.observe(this, {
            attributes: true,
            attributeFilter: ["class"],
        });
 
        const markEngaged = () => {
            this.userEngaged = true;
            observer.disconnect();
        };
        document.addEventListener("pointerdown", markEngaged, {
            once: true,
            capture: true,
        });
        document.addEventListener("keydown", markEngaged, {
            once: true,
            capture: true,
        });
 
        // Catch the case where the class is already present before we attach.
        guardClose();
    }
 
    setHeaderCartIconAccessibility() {
        const cartLink = document.querySelector("#cart-icon-bubble");
        if (cartLink) {
            cartLink.setAttribute("role", "button");
            cartLink.setAttribute("aria-haspopup", "dialog");
        }
 
        // Use event delegation on the document instead of binding directly to the
        // cart icon. An external script (e.g. a checkout/cart app) can re-render or
        // replace the #cart-icon-bubble element after load, which would drop a
        // directly-attached listener and let clicks fall through to the anchor's
        // href="/cart" (i.e. navigate to the cart page instead of opening the
        // drawer). A delegated listener survives any number of icon re-renders.
        document.addEventListener("click", (event) => {
            const trigger =
                event.target.closest && event.target.closest("#cart-icon-bubble");
            if (!trigger) return;
            event.preventDefault();
            this.open(trigger);
        });
 
        document.addEventListener("keydown", (event) => {
            if (event.code.toUpperCase() !== "SPACE") return;
            const trigger =
                event.target.closest && event.target.closest("#cart-icon-bubble");
            if (!trigger) return;
            event.preventDefault();
            this.open(trigger);
        });
    }
 
    open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);
        const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
        if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
            this.setSummaryAccessibility(cartDrawerNote);
 
        // here the animation doesn't seem to always get triggered. A timeout seem to help
        setTimeout(() => {
            this.classList.add("animate", "active");
        });
 
        this.addEventListener(
            "transitionend",
            () => {
                const containerToTrapFocusOn = this.classList.contains("is-empty")
                    ? this.querySelector(".drawer__inner-empty")
                    : document.getElementById("CartDrawer");
                const focusElement =
                    this.querySelector(".drawer__inner") ||
                    this.querySelector(".drawer__close");
                trapFocus(containerToTrapFocusOn, focusElement);
            },
            { once: true }
        );
 
        document.body.classList.add("cart-overflow-hidden");
 
        if (!isSafari()) {
            document.body.classList.add("overflow-hidden");
        } else {
            document.body.classList.add("overflow-clip");
        }
    }
 
    close() {
        this.classList.remove("active");
        removeTrapFocus(this.activeElement);
        document.body.classList.remove("cart-overflow-hidden");
 
        if (!isSafari()) {
            document.body.classList.remove("overflow-hidden");
        } else {
            document.body.classList.remove("overflow-clip");
        }
 
        setTimeout(() => {
            let notification = this.querySelector(".notification");
 
            if (notification) {
                const activeItem = this.querySelector(
                    `.cart-item[data-product-id="${this.productId}"`
                );
                notification.classList.remove("notification-open");
                activeItem?.classList.remove("active");
            }
        }, 1000);
    }
 
    setSummaryAccessibility(cartDrawerNote) {
        cartDrawerNote.setAttribute("role", "button");
        cartDrawerNote.setAttribute("aria-expanded", "false");
 
        if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
            cartDrawerNote.setAttribute(
                "aria-controls",
                cartDrawerNote.nextElementSibling.id
            );
        }
 
        cartDrawerNote.addEventListener("click", (event) => {
            event.currentTarget.setAttribute(
                "aria-expanded",
                !event.currentTarget.closest("details").hasAttribute("open")
            );
        });
 
        cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
    }
 
    renderContents(parsedState) {
        this.querySelector(".drawer__inner").classList.contains("is-empty") &&
            this.querySelector(".drawer__inner").classList.remove("is-empty");
        this.productId = parsedState.id;
        this.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
            sectionElement.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.id],
                section.selector
            );
        });
 
        let notification = this.querySelector(".notification");
 
        if (notification) {
            const activeItem = this.querySelector(
                `.cart-item[data-product-id="${this.productId}"`
            );
            notification.classList.add("notification-open");
            activeItem.classList.add("active");
        }
 
        setTimeout(() => {
            this.querySelector("#CartDrawer-Overlay").addEventListener(
                "click",
                this.close.bind(this)
            );
            this.open();
        });
    }
 
    getSectionInnerHTML(html, selector = ".shopify-section") {
        return new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector(selector).innerHTML;
    }
 
    getSectionsToRender() {
        return [
            {
                id: "cart-drawer",
                selector: "#CartDrawer",
            },
            {
                id: "cart-icon-bubble",
            },
        ];
    }
 
    getSectionDOM(html, selector = ".shopify-section") {
        return new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector(selector);
    }
 
    setActiveElement(element) {
        this.activeElement = element;
    }
}
 
customElements.define("cart-drawer", CartDrawer);
 
class CartDrawerItems extends CartItems {
    getSectionsToRender() {
        return [
            {
                id: "CartDrawer",
                section: "cart-drawer",
                selector: ".drawer__inner",
            },
            {
                id: "cart-icon-bubble",
                section: "cart-icon-bubble",
                selector: ".shopify-section",
            },
        ];
    }
}
 
customElements.define("cart-drawer-items", CartDrawerItems);
 
if (!customElements.get("cart-note")) {
    customElements.define(
        "cart-note",
        class CartNote extends HTMLElement {
            constructor() {
                super();
 
                this.addEventListener(
                    "input",
                    debounce((event) => {
                        const body = JSON.stringify({ note: event.target.value });
                        fetch(`${routes.cart_update_url}`, {
                            ...fetchConfig(),
                            ...{ body },
                        });
                    }, ON_CHANGE_DEBOUNCE_TIMER)
                );
            }
        }
    );
}